/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { Button } from 'reactstrap';
import { Card } from './cards';
import { useAppClickHandlers } from '../../../hooks/useAppClickHandlers';
import DisambiguationModal, { DisambiguationModalProps } from './DisambiguationModal';
import './HomepageCard.scss';

interface HomepageCardProps extends Pick<Card, 'button' | 'btnOutline'> {
  className?: string;
  onClick?: () => void;
  buttonOnClick?: () => void;
  buttonColour?: string;
  children?: JSX.Element | JSX.Element[];
}

const HomepageCard = (props: HomepageCardProps) => {
  const { buttonOnClick, className, button, onClick, btnOutline = true } = props;

  const buttonColour = props.buttonColour ?? 'primary';

  return (
    <div className={`hp-card d-flex flex-column ${className ?? ''} ${onClick ? 'hover' : ''}`} >
      <div className='hp-card-contents flex-fill'>
        {props.children}
      </div>
      {buttonOnClick ? <div className='text-center mt-1'>
        <Button disabled={!!button?.disabled} color={buttonColour} outline={btnOutline} onClick={buttonOnClick}>
          {button?.text ?? 'Access now'}
        </Button>
      </div> : null}
    </div>
  );
}

interface HomepageCardGridProps {
  cards: Card[];
  toggleRequestDemoModal: (props?: { title?: JSX.Element, isPartner?: boolean }) => void;
}

export const HomepageCardGrid = ({ cards, toggleRequestDemoModal }: HomepageCardGridProps) => {

  const [descriptionOpen, setDescriptionOpen] = useState<number | undefined>(undefined);
  const [disambiguationModal, setDisambiguationModal] = useState<DisambiguationModalProps | undefined>(undefined);

  const toggleDescription = (i: number) => setDescriptionOpen(descriptionOpen === i ? undefined : i);
  const externalLink = (link: string) => window.open(link);
  const clickHandlers = useAppClickHandlers({ toggleRequestDemoModal, setDisambiguationModal });

  return (
    <div
      className='hp-card-grid d-flex flex-wrap'>
      {disambiguationModal ? <DisambiguationModal {...disambiguationModal} /> : null}
      {cards
        .map((card, i) => {
          const handler = card.clickHandler ? clickHandlers[card.clickHandler] : undefined;
          const onClick = handler ? () => handler(card) : undefined;
          const buttonOnClick = card.button?.onClickHandler ? clickHandlers[card.button.onClickHandler] : onClick;
          return (
            <HomepageCard
              key={`hp-card-${i}`}
              {...card}
              onClick={onClick}
              buttonOnClick={buttonOnClick}
              className={descriptionOpen === i ? 'expandable-active' : 'expandable-inactive'}
            >
              <div className='h-100 d-flex flex-column'>
                {card.logo ? (
                  <div onClick={onClick}
                    className='hp-card-logo animated text-right d-flex justify-content-center align-items-center position-relative'>
                    <img src={card.logo} alt={'Logo'} width='125' />
                    {card.sponsoredByLogo ?
                      <div className='position-absolute pl-2 pb-1'
                        style={{ bottom: 0, left: 0 }}>
                        <img src={card.sponsoredByLogo} width={70} alt='Sponsor Logo' />
                      </div> : null}
                  </div>
                ) : <></>
                }
                <div
                  className={`${descriptionOpen === i ? '' : 'hp-popup-description-container-hidden'} hp-popup-description-container`}>
                  <div className='hp-popup-arrow-container'>
                    <div className='hp-popup-arrow' />
                  </div>
                  <div className='hp-popup-description'>
                    <h2>{card.title}</h2>
                    {card.description}
                    {card.solutions && card.learnMoreLink ? <Button
                      key={`description-link-${i}`}
                      color='link'
                      onClick={() => window.open(card.learnMoreLink)}
                      className='ml-2'
                    >
                      Learn more
                    </Button> : null}
                    {card.clickHandler ? (
                      <div className='text-right mt-1'>
                        <Button onClick={buttonOnClick as () => void | undefined} >
                          {card.button?.text ?? 'Access now'}
                        </Button>
                      </div>
                    ) : null}
                    {card.partners && (
                      <div className='text-right mt-1'>
                        <Button
                          outline={card.btnOutline ?? true}
                          size='sm'
                          onClick={() => {
                            toggleRequestDemoModal({
                              title: card.title,
                              isPartner: Boolean(card.partners)
                            })
                          }}
                        >
                          {'Get in touch'}
                        </Button>
                      </div>
                    )}
                    <div className='chevron position-absolute'
                      style={{ top: 20, right: 20 }}>
                      <Button color='link-secondary' onClick={() => toggleDescription(i)}>
                        <i className='fa fa-chevron-up' />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className='mt-3 strong'>
                  {card.title}
                </div>

                <div className='mt-2 flex-fill mb-4'>
                  <div className='position-relative'>
                    <div className='card-description'>
                      {card.subtitle}
                    </div>
                    {card.description ?
                      <Button color='link-secondary' className='position-absolute start-0 end-0 card-description-more' onClick={() => toggleDescription(i)}>
                        <i className='fa fa-chevron-down text-md' />
                        <span className='text-sm ml-1'>Read more</span>
                      </Button> : null}
                  </div>
                </div>

                {card.url ? <div className='mt-auto text-center'>
                  <Button className='mt-2' size='sm' color='link'
                    onClick={() => externalLink(`http://${card.url}` ?? '')}>{card.url}</Button>
                </div> : <></>}
              </div>
            </HomepageCard>
          );
        })}
    </div>
  );
}


export default HomepageCard;
